import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";
import useSWR from "swr";
import axios from "axios";
import { SecondaryButtonMediumV2 } from "../../../../../../components/Buttons/Buttons";
import type { ProfileComparisonConfigsResponse } from "../../../../../../types/types";
import type {
  EntityProfile,
  EntityType,
} from "../../../../../../types/types.MDM";
import { endpoints } from "../../../../../../endpoints";
import { useStoreState } from "../../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import { getCompareDisableReason } from "../../../../../SharedPages/OrganizationPage/ProductsList/CompareProducts";
import { getEntityDetailEndpoint } from "../utils";
import type { CompareEntityMeta } from "./compareEntityMeta";
import {
  loadCompareEntityMeta,
  MAX_COMPARE_PRODUCTS,
  MIN_COMPARE_PRODUCTS,
} from "./compareEntityMeta";

const CompareButtonWrap = styled.span`
  display: inline-flex;
  & button:disabled {
    pointer-events: none !important;
  }
`;

type BulkCompareEntitiesProps = {
  entityType: EntityType;
  detailBasePath: string;
  rows: string[];
};

export function BulkCompareEntities({
  entityType,
  detailBasePath,
  rows,
}: BulkCompareEntitiesProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { getLabel } = useMdmEntityDisplayNames();
  const {
    tenant_id,
    storefront_metadata: { enable_profile_comparison },
  } = useStoreState();

  const entityLabelPlural = getLabel(entityType, "plural");
  const noConfigMessage = t(
    "No groups or collections are configured for comparison on this template"
  );
  const [compareMeta, setCompareMeta] = useState<
    Record<string, CompareEntityMeta>
  >(() => loadCompareEntityMeta(entityType));

  useEffect(() => {
    setCompareMeta(loadCompareEntityMeta(entityType));
  }, [rows, entityType]);

  const selectedCount = rows.length;

  const unresolvedIds = useMemo(
    () => rows.filter((id) => !compareMeta[id]?.template_id),
    [rows, compareMeta]
  );

  const shouldLookupTemplates =
    enable_profile_comparison &&
    !!tenant_id &&
    selectedCount >= MIN_COMPARE_PRODUCTS &&
    selectedCount <= MAX_COMPARE_PRODUCTS &&
    unresolvedIds.length > 0;

  const { data: lookedUpTemplateIds } = useSWR(
    shouldLookupTemplates
      ? ["compare-entity-template-ids", entityType, tenant_id, ...rows]
      : null,
    async () => {
      const entries = await Promise.all(
        rows.map(async (id) => {
          const known = compareMeta[id]?.template_id;
          if (known) return [id, known] as const;
          const { data } = await axios.get<EntityProfile>(
            getEntityDetailEndpoint(entityType, tenant_id, id)
          );
          return [id, data.schema?.template_id ?? null] as const;
        })
      );
      return Object.fromEntries(entries) as Record<string, string | null>;
    }
  );

  const templateIds = rows.map(
    (id) => compareMeta[id]?.template_id ?? lookedUpTemplateIds?.[id] ?? null
  );
  const knownTemplateIds = [
    ...new Set(
      templateIds.filter(
        (id): id is string => typeof id === "string" && id.length > 0
      )
    ),
  ];
  const singleTemplateId =
    knownTemplateIds.length === 1 ? knownTemplateIds[0] : null;

  const { data: comparisonConfigs, error: comparisonConfigsError } =
    useSWR<ProfileComparisonConfigsResponse>(
      enable_profile_comparison && tenant_id && singleTemplateId
        ? endpoints.v2_tenants_id_pim_templates_id_settings_profileComparison(
            tenant_id,
            singleTemplateId
          )
        : null
    );

  const templatesLookupDone =
    !shouldLookupTemplates || lookedUpTemplateIds != null;
  const configsFetchDone =
    !singleTemplateId ||
    comparisonConfigs != null ||
    comparisonConfigsError != null;

  const hasComparisonConfig: boolean | null =
    comparisonConfigs != null
      ? (comparisonConfigs.count ?? comparisonConfigs.data?.length ?? 0) > 0
      : comparisonConfigsError
      ? false
      : null;

  const baseDisableReason = getCompareDisableReason(
    selectedCount,
    templateIds,
    t,
    { hasComparisonConfig }
  );
  const disableReason =
    !templatesLookupDone || !configsFetchDone
      ? t("Loading...")
      : hasComparisonConfig === false
      ? noConfigMessage
      : baseDisableReason;
  const compareEnabled =
    !disableReason &&
    templatesLookupDone &&
    configsFetchDone &&
    hasComparisonConfig === true;

  const handleCompare = () => {
    if (!compareEnabled) return;
    const ids = rows.slice(0, MAX_COMPARE_PRODUCTS);
    const params = new URLSearchParams();
    params.set("entities", ids.join(","));
    history.push(`${detailBasePath}/compare?${params.toString()}`);
  };

  if (!enable_profile_comparison || selectedCount === 0) {
    return null;
  }

  return (
    <CompareButtonWrap
      data-tip={disableReason || ""}
      data-for="compare-entities-tooltip"
    >
      <SecondaryButtonMediumV2
        onClick={handleCompare}
        disabled={!compareEnabled}
      >
        {t("Compare {{entityLabel}}", { entityLabel: entityLabelPlural })}
      </SecondaryButtonMediumV2>
      {disableReason && (
        <ReactTooltip id="compare-entities-tooltip" effect="solid" />
      )}
    </CompareButtonWrap>
  );
}
